import { useTranslation } from 'react-i18next';
import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from '@casops/routes/root/components/link/Link';
import RootPageContainer from '../components/root-page-container/RootPageContainer';
import RootPageCard from '../components/root-page-card/RootPageCard';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import {
  FORGOT_PASSWORD_PAGE,
  LOGIN_PAGE,
  PASSWORD_RESET_SUCCESS,
  RESET_PASSWORD_EMAIL,
} from '@casops/shared/constants';
import {
  ResetPasswordModel,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from '@casops/store/apis/authApi';
import { useNavigate } from 'react-router-dom';
import { useCanSubmit } from '@casops/hooks/useCanSubmit';

type FormModel = ResetPasswordModel;

const defaultValues: FormModel = {
  password: '',
  confirmPassword: '',
  email: '',
  token: '',
};

export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const [ResetPasswordChange, { isLoading }] = useResetPasswordMutation();
  const [forgotPasswordChange] = useForgotPasswordMutation();
  const [email, setEmail] = useState('');

  useEffect(() => {
    const storedEmail = sessionStorage.getItem(RESET_PASSWORD_EMAIL);
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate(FORGOT_PASSWORD_PAGE);
    }
  }, [navigate]);

  const schema = useMemo(() => {
    return z
      .object({
        password: z.string().min(1, { message: t('EnterAPassword') }),
        confirmPassword: z.string().min(1, { message: t('ConfirmNewPassword') }),
        token: z.string().min(1, { message: t('EnterSecurityCode') }),
      })
      .refine(data => data.password === data.confirmPassword, {
        message: t('PasswordsDoNotMatch'),
        path: ['confirmPassword'], // This will show the error on the confirmPassword field
      });
  }, [t]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<FormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const { canSubmit, setDisableSubmit } = useCanSubmit(isSubmitting, isLoading);

  const onSubmit = useCallback(
    async (data: FormModel) => {
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      data.email = email;

      await ResetPasswordChange(data)
        .unwrap()
        .then(() => {
          setDisableSubmit(true);
          sessionStorage.setItem(PASSWORD_RESET_SUCCESS, 'true');
          navigate(LOGIN_PAGE);
        })
        .catch(error => {
          setErrorMessage(error.data);
        });
    },
    [ResetPasswordChange, email, navigate, setDisableSubmit],
  );

  const onSendNewCode = useCallback(async () => {
    setErrorMessage(undefined);
    const forgotPasswordData = { email };
    setDisableSubmit(true);

    await forgotPasswordChange(forgotPasswordData)
      .unwrap()
      .then(() => {
        setSuccessMessage(t('SecurityCodeWasResent'));
        setDisableSubmit(false);
      })
      .catch(error => {
        setErrorMessage(error.data);
        setDisableSubmit(false);
      });
  }, [email, setDisableSubmit, forgotPasswordChange, t]);

  return (
    <RootPageContainer>
      <RootPageCard
        subHeading={t('NewPassword')}
        fields={
          <>
            {successMessage && (
              <VdsNotification variant="inline" status="healthy" showCloseButton={false}>
                <span slot="body">{successMessage}</span>
              </VdsNotification>
            )}
            {errorMessage && (
              <VdsNotification variant="inline" status="danger" showCloseButton={false}>
                <span slot="body">{errorMessage}</span>
              </VdsNotification>
            )}
            <CasFormInput
              control={control}
              name="password"
              placeholder={t('NewPassword')}
              type="password"
              disabled={!canSubmit}
              size="large"
            />
            <CasFormInput
              control={control}
              name="confirmPassword"
              placeholder={t('ConfirmNewPassword')}
              type="password"
              disabled={!canSubmit}
              onEnterPressed={() => handleSubmit(onSubmit)()}
              size="large"
            />
            <CasFormInput
              control={control}
              name="token"
              placeholder={t('EnterSecurityCode')}
              type="text"
              disabled={!canSubmit}
              onEnterPressed={() => handleSubmit(onSubmit)()}
              size="large"
            />
          </>
        }
      >
        <VdsButton
          id="save-button"
          variant="primary"
          size="large"
          danger
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmit)}
          isFullWidth
        >
          {t('Save')}
        </VdsButton>
        <VdsButton
          id="send-new-code-button"
          variant="tertiary"
          size="large"
          danger
          disabled={!canSubmit}
          onClick={onSendNewCode}
          isFullWidth
        >
          {t('SendNewCode')}
        </VdsButton>
        <Link to={LOGIN_PAGE} title={t('Cancel')}></Link>
      </RootPageCard>
    </RootPageContainer>
  );
}

export default ResetPassword;
